import type { CommonHandlerProps } from "@circle-react-shared/uikit/TipTapBlockEditor/Toolbar";
import { useVideoClipModal } from "@circle-react/components/shared/VideoClipRecorder/useVideoClipModal";
import { useIsInsideMobileWebview } from "@circle-react/hooks/useIsInsideMobileWebview";
import type { ChangeEvent, ComponentPropsWithRef } from "react";
import { useRef } from "react";
import { isAndroid, isMobile } from "react-device-detect";

import { t } from "@/i18n-js/instance";

interface UseVideoClipRecorderResult {
  nativeVideoCaptureInputProps: ComponentPropsWithRef<"input"> | null;
  showVideoClipRecorder: (props: CommonHandlerProps) => void;
}

export const useVideoClipRecorder = (): UseVideoClipRecorderResult => {
  const videoClipModal = useVideoClipModal();
  const captureInputRef = useRef<HTMLInputElement>(null);
  const onCaptureRef = useRef<((file: File) => void) | null>(null);
  const isInsideMobileWebview = useIsInsideMobileWebview();
  // The mobile app's Android webview (react-native-webview) opens nothing for a
  // video-only input with `capture`. Without it, the app shows its file chooser,
  // which also offers the camera once the app holds the camera permission.
  const shouldRequestCapture = !(isAndroid && isInsideMobileWebview);

  const handleCaptureChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";

    if (file) {
      onCaptureRef.current?.(file);
    }

    onCaptureRef.current = null;
  };

  const showVideoClipRecorder = ({ editor, range }: CommonHandlerProps) => {
    const onInsert = (file: File) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent({
          type: "file-loader",
          attrs: { file, label: t("tiptap.slash_commands.video_clip") },
        })
        .run();
    };

    if (isMobile) {
      onCaptureRef.current = onInsert;
      captureInputRef.current?.click();
      return;
    }

    void videoClipModal.show({ onInsert });
  };

  return {
    nativeVideoCaptureInputProps: isMobile
      ? {
          ref: captureInputRef,
          type: "file",
          accept: "video/*",
          capture: shouldRequestCapture ? "user" : undefined,
          hidden: true,
          tabIndex: -1,
          "aria-hidden": true,
          onChange: handleCaptureChange,
        }
      : null,
    showVideoClipRecorder,
  };
};
